import { FC } from 'react';
import { MultiSelect, NumberInput, Typography, UploadFile } from 'team-hero-ui';
import { Control, Controller } from 'react-hook-form';

import type { IApplicationFormState } from '../../JobAdApplyForm.interface';
import type { IRowItem } from '../../interfaces/FormConfig';
import LocalizedDatePicker from 'components/LocalizedDatePicker';
import { UploadImage } from './components/UploadImage/UploadImage';
import {
  FullWidthTextInputWrapper,
  FullWidthWrapper,
  HelperTextRow,
} from './FormItem.styled';
import EmptyModalFieldStyled from 'components/Modals/AddEditModal/ModalSection/fields/EmptyModalFieldStyled';
import ModalValidationWrapper from 'components/Modals/AddEditModal/ModalValidationWrapper';
import TextModalField from 'components/Modals/AddEditModal/ModalSection/fields/TextModalField';
import SwitchField from 'components/Modals/AddEditModal/ModalSection/fields/SwitchField';
import SelectField from 'components/Modals/AddEditModal/ModalSection/fields/SelectField';
import { useGetCountrySelectOptions } from 'hooks/countryList/useGetCountrySelectOptions.hook';
import PicturesField from 'components/Modals/AddEditModal/ModalSection/fields/PicturesField';
import { useFileMimeType } from 'hooks/useFileMimeType.hook';
import SelectInput from 'components/Modals/AddEditModal/ModalSection/fields/SelectInputField/SelectInput';

export interface IFormItemProps {
  element: IRowItem;
  control: Control<IApplicationFormState>;
}

export const FormItem: FC<IFormItemProps> = ({ element, control }) => {
  const { acceptDocumentFiles, acceptImageFiles } = useFileMimeType();

  const { getOptions, countriesOptionGroups } = useGetCountrySelectOptions();

  const styleType = 'formUniversal';

  const label =
    element.required && element.renderType !== 'switch'
      ? (element.label || '') + '*'
      : element.label || '';

  switch (element.renderType) {
    case 'text':
    case 'phone':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          defaultValue={''}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <FullWidthTextInputWrapper>
                <TextModalField
                  label={label}
                  value={value}
                  onChange={onChange}
                  styleType={styleType}
                  mask={element.mask}
                  hint={element.hint}
                  endAdornment={element.endAdornment}
                  inputRef={ref}
                />
              </FullWidthTextInputWrapper>
            </ModalValidationWrapper>
          )}
        />
      );
    case 'number':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          defaultValue={''}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <NumberInput
                min={0}
                label={label}
                value={value}
                onChange={onChange}
                styleType='formUniversal'
                helperText={element.hint}
                inputRef={ref}
              />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'float':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          defaultValue={''}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <NumberInput
                label={label}
                value={value}
                min={0}
                onChange={onChange}
                decimalRound={10}
                styleType='formUniversal'
                helperText={element.hint}
                inputRef={ref}
              />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'selectInput':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <SelectInput
              validationText={error?.message}
              key={element.propertyName}
              label={label}
              value={value}
              onChange={onChange}
              options={element.options}
              externalDataSource={element.externalDataSource}
              enableEmptySelect={!element.required}
              styleType='formUniversal'
              helperText={element.hint}
              inputRef={ref}
              hideValidationBorder
            />
          )}
        />
      );
    case 'select':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <SelectField
              validationText={error?.message}
              key={element.propertyName}
              label={label}
              options={element.options || getOptions(element.propertyName)}
              externalDataSource={element.externalDataSource}
              onChange={onChange}
              value={value}
              styleType='formUniversal'
              hint={element.hint}
              enableEmptySelect={!element.required}
              hideValidationBorder
              inputRef={ref}
            />
          )}
        />
      );
    case 'selectCountry':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <SelectField
              hideValidationBorder
              validationText={error?.message}
              key={element.propertyName}
              label={label}
              options={getOptions('addressCountry')}
              externalDataSource={element.externalDataSource}
              onChange={onChange}
              value={value}
              styleType='formUniversal'
              hint={element.hint}
              enableEmptySelect={!element.required}
              optionGroups={countriesOptionGroups}
              inputRef={ref}
              withAutocomplete
            />
          )}
        />
      );
    case 'date':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <FullWidthWrapper>
                <LocalizedDatePicker
                  label={label}
                  value={value}
                  onChange={onChange}
                  type='date'
                  styleType={styleType}
                  helperText={element.hint}
                  inputRef={ref}
                />
              </FullWidthWrapper>
            </ModalValidationWrapper>
          )}
        />
      );
    case 'image':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <ModalValidationWrapper text={error?.message}>
              <UploadImage onChange={onChange} value={value} label={label} />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'images':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          defaultValue={[]}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper text={error?.message}>
              <PicturesField
                onChange={onChange}
                value={value}
                maxImages={4}
                accept={acceptImageFiles}
                styleType={styleType}
                helperText={element.hint}
                inputRef={ref}
              />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'multiselect':
      // TODOM:Add logic for external data type
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({ field: { ref, onChange, value } }) => (
            <MultiSelect
              inputLabel={label}
              onChange={onChange}
              items={value || element.multiselectItems}
              styleType={styleType}
              helperText={element.hint}
              inputRef={ref}
            />
          )}
        />
      );
    case 'switch':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <ModalValidationWrapper text={error?.message}>
              <SwitchField
                checked={value}
                label={label}
                onChange={onChange}
                hint={element.hint}
                styleType='bright'
                isNullable={!element.required}
              />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'emptySpace':
      return <EmptyModalFieldStyled />;
    case 'file':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <UploadFile
                onChange={onChange}
                value={value}
                elementListLabel={label}
                label={label}
                maxItems={1}
                styleType={'formUniversal'}
                iconPosition={'left'}
                minHeight={58}
                accept={acceptDocumentFiles}
                helperText={element.hint}
                inputRef={ref}
              />
            </ModalValidationWrapper>
          )}
        />
      );
    case 'textarea':
      return (
        <Controller
          key={element.propertyName}
          name={element.propertyName}
          control={control}
          render={({
            field: { ref, onChange, value },
            fieldState: { error },
          }) => (
            <ModalValidationWrapper hideBorder text={error?.message}>
              <FullWidthTextInputWrapper>
                <TextModalField
                  label={label}
                  value={value}
                  onChange={onChange}
                  styleType={styleType}
                  mask={element.mask}
                  hint={element.hint}
                  multiline
                  minRows={3}
                  inputRef={ref}
                />
              </FullWidthTextInputWrapper>
            </ModalValidationWrapper>
          )}
        />
      );
    case 'helperText':
      return (
        <HelperTextRow>
          <Typography.H4>{element.helperText}</Typography.H4>
        </HelperTextRow>
      );
    default:
      return null;
  }
};
